/*eslint max-lines-per-function: ["error", 180]*/
/*eslint complexity: ["error", 22]*/
import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';

import FormRow from '../../../app/components/FormRow';
import {
  CreateUpdateProjectWithTeamDto,
  GetProjectDto,
} from '../../../common/pokCore/autogenerated/pokApiClient/models';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { useTranslation } from '../../../common/hooks/useTranslation';
import InternalProjectSelector from '../Selects/InternalProjectSelector';
import { ResponseError } from '../../../common/pokCore/autogenerated/pokApiClient';
import { isPendingDateChangeApplication } from '../../../common/pokCore/contexts/ProjectContext';

import { ProjectApplicationForm } from './ProjectApplicationForm';
import { ProjectMainFieldsForm } from './ProjectMainFieldsForm';
import { ProjectMainFieldsForm2 } from './ProjectMainFieldsForm2';
import { ProjectMainFieldsForm3 } from './ProjectMainFieldsForm3';

interface ProjectFormProps {
  project: CreateUpdateProjectWithTeamDto;
  readOnly?: boolean;
  isCreatedFromOrder?: boolean;
  orderPhaseId?: string;
  budgetId?: string;
  budgetName?: string;
  show?: boolean;
  propertyChange?: (obj: Partial<CreateUpdateProjectWithTeamDto>) => void;
  onShowApplicationFormChange?: (show: boolean) => void;
}

export const ProjectForm: React.FC<ProjectFormProps> = props => {
  const [internalProjects, setInternalProjects] = useState<GetProjectDto[]>([]);
  const [showDelayedForm, setShowDelayedForm] = useState<boolean>(false);
  const [showDateChangeForm, setShowDateChangeForm] = useState<boolean>(false);

  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('projectLng');

  useEffect(() => {
    let mounted = true;
    if (props.project.purchaserId) {
      pok.projects
        .findInternalProjectsByPurchaserForCompanyAndTeams(
          props.project.purchaserId,
          props.project.teamIds,
        )
        .then(res => {
          if (mounted) {
            setInternalProjects(res);
          }
        })
        .catch(err => {
          notifications.caughtError(err);
        });
    }

    return () => {
      mounted = false;
      setInternalProjects([]);
    };
  }, [
    notifications,
    pok.projects,
    props.project.purchaserId,
    props.project.teamIds,
  ]);

  useEffect(() => {
    if (props.project.startDate && !props.project.id && props.show) {
      pok.projects
        .isDelayed(props.project.startDate)
        .then(res => {
          setShowDelayedForm(res.delayed);
          props.onShowApplicationFormChange?.(res.delayed);
          if (res.delayed && props.propertyChange) {
            props.propertyChange({
              delayVerification: true,
            });
          }
        })
        .catch(err => {
          notifications.caughtError(err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.show]);

  const datesChanged = async (dates: { startDate?: Date; endDate?: Date }) => {
    if (!props.propertyChange) {
      return;
    }
    try {
      if (props.project.id) {
        const applicationRequired =
          await pok.projects.isApplicationRequiredForDateChange(
            props.project,
            dates.startDate,
            dates.endDate,
          );
        setShowDateChangeForm(applicationRequired);
        props.onShowApplicationFormChange?.(applicationRequired);
        if (dates.startDate) {
          props.propertyChange({
            startDate: dates.startDate,
            dateChangeVerification: applicationRequired,
            newStartDate: dates.startDate,
            dateChangeMessage: applicationRequired
              ? props.project.dateChangeMessage
              : undefined,
          });
        } else if (dates.endDate) {
          props.propertyChange({
            endDate: dates.endDate,
            dateChangeVerification: applicationRequired,
            newEndDate: dates.endDate,
            dateChangeMessage: applicationRequired
              ? props.project.dateChangeMessage
              : undefined,
          });
        }
      } else {
        if (dates.startDate) {
          const delayed = await pok.projects.isDelayed(dates.startDate);
          setShowDelayedForm(delayed.delayed);
          props.onShowApplicationFormChange?.(delayed.delayed);
          props.propertyChange({
            startDate: dates.startDate,
            delayVerification: delayed.delayed,
          });
        } else if (dates.endDate) {
          props.propertyChange({
            endDate: dates.endDate,
          });
        }
      }
    } catch (error) {
      notifications.caughtError(error as ResponseError);
    }
  };

  return (
    <Form className="d-grid gap-4 mb-3">
      <ProjectMainFieldsForm
        project={props.project}
        readOnly={props.readOnly}
        propertyChange={props.propertyChange}
        isCreatedFromOrder={props.isCreatedFromOrder}
        budgetId={props.budgetId}
      />
      <ProjectMainFieldsForm2
        project={props.project}
        readOnly={props.readOnly}
        propertyChange={props.propertyChange}
        isCreatedFromOrder={props.isCreatedFromOrder}
        budgetId={props.budgetId}
        budgetName={props.budgetName}
      />
      {(internalProjects.length > 0 || props.project.parentId) && (
        <FormRow
          controlId="internalProject"
          label={t(tk.prLang.internalProject)}
        >
          <InternalProjectSelector
            projects={internalProjects}
            readOnly={props.readOnly || props.isCreatedFromOrder}
            value={props.project.parentId || ''}
            onChange={option =>
              props.propertyChange &&
              props.propertyChange({
                parentId: option?.value,
              })
            }
          />
        </FormRow>
      )}
      <ProjectMainFieldsForm3
        project={props.project}
        readOnly={props.readOnly}
        propertyChange={props.propertyChange}
        isCreatedFromOrder={props.isCreatedFromOrder}
        orderPhaseId={props.orderPhaseId}
        budgetId={props.budgetId}
        datesChanged={datesChanged}
        blockedDates={isPendingDateChangeApplication(props.project)}
      />
      {(showDelayedForm || showDateChangeForm) && (
        <ProjectApplicationForm
          project={props.project}
          readOnly={props.readOnly}
          propertyChange={props.propertyChange}
          showDateChangeForm={showDateChangeForm}
        />
      )}
    </Form>
  );
};
